import { messageErreur } from '@imp/shared/message-erreur';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Image as ImageIcon, Pencil, Plus, Users, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { EquipesManager } from '@/modules/agenda/AgendaPage';
import {
  createEmploye,
  type EmployeAdmin,
  type Equipe,
  listEmployesAdmin,
  listEquipes,
  updateEmploye,
} from '@/modules/agenda/api';
import { MenuEntrepriseCard } from '@/modules/personnalisation/MenuEntrepriseCard';
import { getOrganisation, updateApparence } from '@/modules/settings/api';

const DEFAUT_PRIMAIRE = '#E1501A';
const DEFAUT_SECONDAIRE = '#1D4595';

// Lit une image, la redimensionne (largeur max) et renvoie une data-URL PNG
// — stockée telle quelle dans organizations.logo_url (pas de bucket requis).
function fileToDataUrl(file: File, maxW = 360): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => {
      reject(new Error('Lecture du fichier impossible'));
    };
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => {
        reject(new Error('Image invalide'));
      };
      img.onload = () => {
        const scale = Math.min(1, maxW / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas indisponible'));
          return;
        }
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/png'));
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
}

export function PersonnalisationPage() {
  const queryClient = useQueryClient();
  const orgQ = useQuery({ queryKey: ['organisation'], queryFn: getOrganisation });
  const equipesQ = useQuery({ queryKey: ['equipes'], queryFn: listEquipes });
  const employesQ = useQuery({ queryKey: ['employes-admin'], queryFn: listEmployesAdmin });

  const [primaire, setPrimaire] = useState(DEFAUT_PRIMAIRE);
  const [secondaire, setSecondaire] = useState(DEFAUT_SECONDAIRE);
  const [logo, setLogo] = useState<string | null>(null);
  const [logoErr, setLogoErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const [gestionEquipes, setGestionEquipes] = useState(false);
  const [editEmploye, setEditEmploye] = useState<EmployeAdmin | 'nouveau' | null>(null);

  useEffect(() => {
    const o = orgQ.data;
    if (!o) return;
    setPrimaire(o.couleur_primaire ?? DEFAUT_PRIMAIRE);
    setSecondaire(o.couleur_secondaire ?? DEFAUT_SECONDAIRE);
    setLogo(o.logo_url ?? null);
  }, [orgQ.data]);

  const apparenceMut = useMutation({
    mutationFn: () =>
      updateApparence({ couleur_primaire: primaire, couleur_secondaire: secondaire, logo_url: logo }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['organisation'] });
    },
  });

  const onLogoFile = (file: File | undefined): void => {
    setLogoErr(null);
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setLogoErr('Choisissez un fichier image (PNG, JPG, SVG…).');
      return;
    }
    fileToDataUrl(file)
      .then((url) => {
        setLogo(url);
      })
      .catch((e: unknown) => {
        setLogoErr(e instanceof Error ? e.message : 'Erreur de traitement de l’image');
      });
  };

  const equipes = equipesQ.data ?? [];
  const equipeNom = (id: string | null): string =>
    id ? (equipes.find((e) => e.id === id)?.nom ?? '—') : '—';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Personnalisation</h1>
        <p className="text-muted-foreground">
          Couleurs, logo, menu, équipes et intervenants de votre société.
        </p>
      </div>

      {/* ---------- Apparence ---------- */}
      <Card>
        <CardHeader>
          <CardTitle>Apparence</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label>Logo</Label>
            <div className="flex items-center gap-4">
              <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted/30">
                {logo ? (
                  <img src={logo} alt="Logo" className="max-h-full max-w-full object-contain" />
                ) : (
                  <ImageIcon className="h-7 w-7 text-muted-foreground" />
                )}
              </div>
              <div className="space-y-1.5">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    onLogoFile(e.target.files?.[0]);
                  }}
                />
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileRef.current?.click()}
                  >
                    Choisir un logo
                  </Button>
                  {logo ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setLogo(null);
                      }}
                    >
                      Retirer
                    </Button>
                  ) : null}
                </div>
                <p className="text-xs text-muted-foreground">
                  Apparaît dans l’en-tête de l’app et sur vos devis / factures.
                </p>
                {logoErr ? <p className="text-xs text-destructive">{logoErr}</p> : null}
              </div>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="coul_prim">Couleur principale</Label>
              <div className="flex items-center gap-2">
                <input
                  id="coul_prim"
                  type="color"
                  value={primaire}
                  onChange={(e) => {
                    setPrimaire(e.target.value);
                  }}
                  className="h-12 w-14 shrink-0 cursor-pointer rounded-md border border-input lg:h-11"
                />
                <Input
                  value={primaire}
                  onChange={(e) => {
                    setPrimaire(e.target.value);
                  }}
                  className="w-28 font-mono"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="coul_sec">Couleur secondaire</Label>
              <div className="flex items-center gap-2">
                <input
                  id="coul_sec"
                  type="color"
                  value={secondaire}
                  onChange={(e) => {
                    setSecondaire(e.target.value);
                  }}
                  className="h-12 w-14 shrink-0 cursor-pointer rounded-md border border-input lg:h-11"
                />
                <Input
                  value={secondaire}
                  onChange={(e) => {
                    setSecondaire(e.target.value);
                  }}
                  className="w-28 font-mono"
                />
              </div>
            </div>
          </div>

          {/* Aperçu */}
          <div className="flex items-center gap-3 rounded-md border p-3">
            <span className="text-xs font-medium text-muted-foreground">Aperçu :</span>
            <span
              className="rounded px-3 py-1 text-sm font-semibold text-white"
              style={{ backgroundColor: primaire }}
            >
              Principale
            </span>
            <span
              className="rounded px-3 py-1 text-sm font-semibold text-white"
              style={{ backgroundColor: secondaire }}
            >
              Secondaire
            </span>
          </div>

          <div className="flex items-center gap-3">
            <Button
              disabled={apparenceMut.isPending}
              onClick={() => {
                apparenceMut.mutate();
              }}
            >
              {apparenceMut.isPending ? 'Enregistrement…' : 'Enregistrer l’apparence'}
            </Button>
            {apparenceMut.isSuccess ? (
              <span className="text-sm text-green-700 dark:text-green-400">Enregistré ✓</span>
            ) : null}
            {apparenceMut.isError ? (
              <span className="text-sm text-destructive">
                {messageErreur(apparenceMut.error)}
              </span>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {/* ---------- Menu de l’entreprise ---------- */}
      <MenuEntrepriseCard />

      {/* ---------- Équipes ---------- */}
      <Card>
        <CardHeader>
          <CardTitle>Équipes</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Regroupez vos couvreurs en équipes (avec une couleur) pour les affecter aux chantiers et
            à l’agenda.
          </p>
          <div className="flex flex-wrap gap-2">
            {equipes.length === 0 ? (
              <span className="text-sm text-muted-foreground">Aucune équipe pour le moment.</span>
            ) : (
              equipes.map((eq: Equipe) => (
                <span
                  key={eq.id}
                  className="flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm"
                >
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: eq.couleur }}
                  />
                  {eq.nom}
                </span>
              ))
            )}
          </div>
          <Button variant="outline" onClick={() => { setGestionEquipes(true); }}>
            <Users className="h-4 w-4" /> Gérer les équipes
          </Button>
        </CardContent>
      </Card>

      {/* ---------- Intervenants ---------- */}
      <Card>
        <CardHeader>
          <CardTitle>Intervenants (couvreurs)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              Vos compagnons / chefs de chantier, avec leur couleur d’agenda et leur équipe.
            </p>
            <Button onClick={() => { setEditEmploye('nouveau'); }}>
              <Plus className="h-4 w-4" /> Nouvel intervenant
            </Button>
          </div>
          <div className="divide-y rounded-md border">
            {(employesQ.data ?? []).length === 0 ? (
              <p className="p-4 text-sm text-muted-foreground">Aucun intervenant.</p>
            ) : (
              (employesQ.data ?? []).map((e) => (
                <div key={e.id} className="flex items-center gap-3 px-3 py-2">
                  <span
                    className="h-3 w-3 shrink-0 rounded-full"
                    style={{ backgroundColor: e.couleur_agenda ?? '#64748b' }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">
                      {e.prenom} {e.nom}
                      {!e.actif ? (
                        <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">
                          inactif
                        </span>
                      ) : null}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {e.poste ?? '—'} · Équipe : {equipeNom(e.equipe_id)}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Modifier"
                    onClick={() => { setEditEmploye(e); }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {gestionEquipes ? (
        <EquipesManager onClose={() => { setGestionEquipes(false); }} />
      ) : null}

      {editEmploye ? (
        <EmployeForm
          employe={editEmploye === 'nouveau' ? null : editEmploye}
          equipes={equipes}
          onClose={() => { setEditEmploye(null); }}
          onSaved={() => {
            setEditEmploye(null);
            void queryClient.invalidateQueries({ queryKey: ['employes-admin'] });
            void queryClient.invalidateQueries({ queryKey: ['employes'] });
          }}
        />
      ) : null}
    </div>
  );
}

// Modale de création / édition d'un intervenant.
function EmployeForm({
  employe,
  equipes,
  onClose,
  onSaved,
}: {
  employe: EmployeAdmin | null;
  equipes: Equipe[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [prenom, setPrenom] = useState(employe?.prenom ?? '');
  const [nom, setNom] = useState(employe?.nom ?? '');
  const [poste, setPoste] = useState(employe?.poste ?? '');
  const [couleur, setCouleur] = useState(employe?.couleur_agenda ?? '#2563eb');
  const [equipeId, setEquipeId] = useState(employe?.equipe_id ?? '');
  const [actif, setActif] = useState(employe?.actif ?? true);

  const mut = useMutation({
    mutationFn: async () => {
      const base = {
        nom: nom.trim(),
        prenom: prenom.trim(),
        poste,
        couleur_agenda: couleur,
        equipe_id: equipeId === '' ? null : equipeId,
      };
      if (employe) await updateEmploye(employe.id, { ...base, actif });
      else await createEmploye(base);
    },
    onSuccess: onSaved,
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg border bg-card p-5"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            {employe ? 'Modifier l’intervenant' : 'Nouvel intervenant'}
          </h2>
          <Button variant="ghost" size="icon" aria-label="Fermer" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="emp_prenom">Prénom</Label>
              <Input
                id="emp_prenom"
                value={prenom}
                onChange={(e) => {
                  setPrenom(e.target.value);
                }}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="emp_nom">Nom</Label>
              <Input
                id="emp_nom"
                value={nom}
                onChange={(e) => {
                  setNom(e.target.value);
                }}
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="emp_poste">Poste</Label>
            <Input
              id="emp_poste"
              value={poste}
              placeholder="Chef de chantier, Couvreur, Compagnon…"
              onChange={(e) => {
                setPoste(e.target.value);
              }}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="emp_equipe">Équipe</Label>
              <Select
                id="emp_equipe"
                value={equipeId}
                onChange={(e) => {
                  setEquipeId(e.target.value);
                }}
              >
                <option value="">— Sans équipe —</option>
                {equipes.map((eq) => (
                  <option key={eq.id} value={eq.id}>
                    {eq.nom}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="emp_couleur">Couleur d’agenda</Label>
              <input
                id="emp_couleur"
                type="color"
                value={couleur}
                onChange={(e) => {
                  setCouleur(e.target.value);
                }}
                className="h-12 w-full cursor-pointer rounded-md border border-input lg:h-11"
              />
            </div>
          </div>
          {employe ? (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={actif}
                onChange={(e) => {
                  setActif(e.target.checked);
                }}
              />
              Actif (décocher pour archiver l’intervenant)
            </label>
          ) : null}
          {mut.isError ? (
            <p className="text-sm text-destructive">{messageErreur(mut.error)}</p>
          ) : null}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button
            disabled={mut.isPending || nom.trim() === '' || prenom.trim() === ''}
            onClick={() => {
              mut.mutate();
            }}
          >
            {mut.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </div>
      </div>
    </div>
  );
}
